import React, { useMemo, useState, useCallback } from 'react';
import { BackPill } from './BackPill';
import { ChevronLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useHistoryClose } from '../../hooks/useHistoryClose';

/* ═══════════════════════════════════════════════════════════════════
   THE TRACKER — Imperial Evolution / Aura
   Two sub-tabs:
     · Aura Input  → colored 1-10 boxes for 6 pillars + "Aura Result" btn
     · Aura Result → dual mini-graph, Today (top) vs Yesterday (bottom)
   Zero-cost.  Persisted at localStorage['bliss_tracker_v1'].
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const GOLD_BRIGHT = '#E9C77A';
const BG = '#050308';

/* 6 Aura pillars — accent colors match the TryLauncher tile-icon glows exactly:
   Fuel=emerald, Power=ruby, Force=sapphire, Energy=amethyst, Aegis=orange,
   Destiny=cobalt.  Used for the chart x-axis labels + baseline (bars remain
   score-ramped red→green so magnitude reads at a glance). */
const AURA = [
  { key: 'fuel',    label: 'Fuel',    accent: '#5BE08A' },   // emerald  — Fuel tile
  { key: 'power',   label: 'Power',   accent: '#FF6478' },   // ruby     — Power tile
  { key: 'force',   label: 'Force',   accent: '#5DD4FF' },   // sapphire — Force tile
  { key: 'energy',  label: 'Energy',  accent: '#B978FF' },   // amethyst — Energy tile
  { key: 'aegis',   label: 'Aegis',   accent: '#FFB265' },   // orange   — Aegis tile
  { key: 'destiny', label: 'Destiny', accent: '#7C9BFF' },   // cobalt   — Destiny tile
];

/* 10-step red→green ramp (index 0 = score 1) */
const SCORE_COLORS = [
  '#7A0000', // 1 dark red
  '#B01F1F', // 2 red
  '#E24A1F', // 3 orange-red
  '#EE8A22', // 4 orange
  '#F0C93A', // 5 yellow
  '#C9D040', // 6 yellow-green
  '#8ED847', // 7 light green
  '#47B84D', // 8 green
  '#2AAF54', // 9 emerald
  '#148F3C', // 10 deep green
];

const STORE_KEY = 'bliss_tracker_v1';
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const readStore = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
};
const writeStore = (o) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(o)); } catch { /* noop */ }
};
const emptyScores = () => AURA.reduce((a, p) => ({ ...a, [p.key]: 0 }), {});
const avg6 = (s) => (AURA.reduce((sum, p) => sum + (s?.[p.key] || 0), 0)) / AURA.length;

/* ────────────── SCORE ROW — 10 colored boxes ────────────── */
const ScoreRow = ({ pillar, value, onChange }) => (
  <div className="rounded-xl px-2.5 py-2"
    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${pillar.accent}55` }}>
    <div className="flex items-center justify-between mb-1.5">
      <p className="font-['Cinzel'] tracking-[0.16em]"
        style={{ color: pillar.accent, fontSize: 12,
          textShadow: `0 0 8px ${pillar.accent}66` }}>
        {pillar.label.toUpperCase()}
      </p>
      <p className="font-['Cinzel']"
        style={{ color: value ? SCORE_COLORS[value - 1] : 'rgba(255,255,255,0.35)',
          fontSize: 14 }}>
        {value || '–'}<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>/10</span>
      </p>
    </div>
    <div className="grid grid-cols-10 gap-[3px]">
      {SCORE_COLORS.map((c, i) => {
        const n = i + 1;
        const active = value === n;
        const dimmed = value && !active;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(pillar.key, n)}
            data-testid={`tracker-score-${pillar.key}-${n}`}
            className="rounded-[3px] font-['Cinzel'] transition-all flex items-center justify-center"
            style={{
              height: 22,
              background: active ? c : dimmed ? `${c}44` : `${c}88`,
              color: active ? '#0a0a0a' : dimmed ? 'rgba(255,255,255,0.5)' : '#ffffff',
              fontSize: 9,
              fontWeight: active ? 700 : 500,
              border: active ? `1.5px solid #fff` : `1px solid ${c}66`,
              boxShadow: active ? `0 0 8px ${c}, inset 0 0 3px rgba(255,255,255,0.35)` : 'none',
              transform: active ? 'scale(1.1)' : 'scale(1)',
              lineHeight: 1,
              padding: 0,
            }}>
            {n}
          </button>
        );
      })}
    </div>
  </div>
);

/* ────────────── RESULT GRAPH (X = pillars, Y = 0–10)
   Clean axis-based bar chart — matches reference: Y-axis with tick marks
   & numeric labels 0–10, X-axis with pillar names, vertical bars. */
const ResultGraph = ({ title, scores, compareTo, positiveWhenHigher = true }) => {
  const Y_TICKS = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0]; // ALL numbers + 0 baseline
  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-xl px-2 py-1.5"
      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${GOLD}33` }}
      data-testid={`tracker-result-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between flex-shrink-0 mb-0.5">
        <p className="font-['Outfit'] uppercase tracking-[0.28em]"
          style={{ color: GOLD, fontSize: 9 }}>
          {title} <span style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>· Aura Level</span>
        </p>
        {/* Comparison legend chip — only visible on Today (compareTo present) */}
        {compareTo && positiveWhenHigher && (
          <div className="flex items-center gap-1.5" data-testid="tracker-legend">
            {[
              { c: '#E24A1F', l: 'Low' },
              { c: '#F0C93A', l: 'Same' },
              { c: '#2AAF54', l: 'High' },
            ].map((it) => (
              <span key={it.l} className="flex items-center gap-[3px]">
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: it.c,
                  boxShadow: `0 0 4px ${it.c}CC`,
                }}/>
                <span className="font-['Outfit'] uppercase"
                  style={{ color: it.c, fontSize: 7, letterSpacing: '0.1em' }}>
                  {it.l}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Chart body */}
      <div className="flex-1 min-h-0 flex">
        {/* Y-axis: labels + axis line */}
        <div className="flex-shrink-0 flex" style={{ width: 18 }}>
          <div className="flex-1 flex flex-col justify-between pr-0.5 items-end">
            {Y_TICKS.map((n) => (
              <span key={n} className="font-['Outfit']"
                style={{
                  color: n === 0 ? `${GOLD}` : 'rgba(255,255,255,0.55)',
                  fontSize: 7,
                  lineHeight: 1,
                  fontWeight: n === 0 ? 700 : 400,
                }}>
                {n}
              </span>
            ))}
          </div>
          <div style={{ width: 1, background: `${GOLD}66`, alignSelf: 'stretch' }}/>
        </div>

        {/* Bars area — height mapped exactly to (v/10) so 0 sits on baseline */}
        <div className="flex-1 flex items-end justify-around gap-1 pl-1 pr-0.5 relative">
          {/* horizontal gridlines aligned to every Y-tick */}
          {Y_TICKS.map((n) => (
            <div key={n} className="absolute left-0 right-0 pointer-events-none"
              style={{
                bottom: `${(n / 10) * 100}%`,
                borderTop: n === 0
                  ? `1px solid ${GOLD}66`
                  : '1px dashed rgba(255,255,255,0.05)',
                height: 0,
              }}/>
          ))}
          {AURA.map((p) => {
            const v = scores?.[p.key] || 0;
            const other = compareTo?.[p.key] || 0;
            // Bars ALWAYS use the 1-10 red→green ramp (mirrors input boxes).
            const barColor = v ? SCORE_COLORS[v - 1] : 'rgba(255,255,255,0.15)';
            const glow = v ? `0 0 6px ${barColor}88` : 'none';
            // Comparison legend chip (Today only): red = lower, yellow = equal, green = higher.
            let chipColor = null;
            if (compareTo && positiveWhenHigher && v > 0) {
              if (v > other)      chipColor = '#2AAF54';
              else if (v < other) chipColor = '#E24A1F';
              else                chipColor = '#F0C93A';
            }
            return (
              <div key={p.key} className="flex-1 flex flex-col items-center justify-end h-full"
                data-testid={`tracker-bar-${title.toLowerCase()}-${p.key}`}>
                <span className="flex items-center gap-[3px]"
                  style={{ lineHeight: 1, marginBottom: 1 }}>
                  {chipColor && (
                    <span data-testid={`tracker-chip-${p.key}`}
                      style={{
                        width: 5, height: 5, borderRadius: '50%',
                        background: chipColor,
                        boxShadow: `0 0 4px ${chipColor}CC`,
                        display: 'inline-block',
                      }}/>
                  )}
                  <span className="font-['Cinzel']"
                    style={{
                      color: v ? barColor : 'rgba(255,255,255,0.3)',
                      fontSize: 8, lineHeight: 1,
                      textShadow: v ? `0 0 4px ${barColor}66` : 'none',
                    }}>
                    {v || '0'}
                  </span>
                </span>
                <div className="rounded-t"
                  style={{
                    width: '65%',
                    height: `${(v / 10) * 100}%`,
                    background: `linear-gradient(180deg, ${barColor}, ${barColor}77)`,
                    boxShadow: glow,
                    minHeight: v > 0 ? 2 : 0,
                    transition: 'height 400ms ease-out',
                  }}/>
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels (X-axis line is drawn as the 0-tick gridline above) */}
      <div className="flex-shrink-0" style={{ paddingLeft: 18 }}>
        <div className="flex mt-0.5 pl-1">
          {AURA.map((p) => (
            <span key={p.key} className="flex-1 text-center font-['Outfit'] truncate"
              style={{ color: p.accent, fontSize: 8, letterSpacing: '0.04em' }}>
              {p.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ────────────── ROOT MODAL ────────────── */
export const TheTracker = ({ onClose }) => {
  const [tab, setTab] = useState('input'); // 'input' | 'result'
  // Back walks: result → input → close.
  useHistoryClose(true, useCallback(() => {
    if (tab !== 'input') {
      setTab('input');
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) {}
    } else {
      onClose();
    }
  }, [tab, onClose]));
  const [store, setStore] = useState(readStore);
  const key = todayKey();

  const todayEntry = store[key] || null;
  const yesterdayEntry = useMemo(() => {
    const keys = Object.keys(store).filter(k => k !== key).sort();
    return keys.length ? store[keys[keys.length - 1]] : null;
  }, [store, key]);

  const [scores, setScores] = useState(todayEntry?.scores || emptyScores());
  const anyScored = AURA.some(p => (scores[p.key] || 0) > 0);
  const allScored = AURA.every(p => (scores[p.key] || 0) > 0);

  const setScore = (k, v) => setScores((s) => ({ ...s, [k]: v }));

  const goToResult = () => {
    // Persist today's scores before jumping to result
    if (anyScored) {
      const next = { ...store, [key]: { scores, ts: Date.now() } };
      setStore(next);
      writeStore(next);
    }
    setTab('result');
  };

  const clearToday = () => {
    if (!window.confirm('Clear today\'s Aura scores?')) return;
    const next = { ...store }; delete next[key];
    setStore(next); writeStore(next);
    setScores(emptyScores());
  };

  /* ── Verdict summary text ── */
  const verdict = useMemo(() => {
    const todayScores = todayEntry?.scores || scores;
    const yScores = yesterdayEntry?.scores;
    if (!anyScored && !yScores) return null;
    if (!yScores) return { kind: 'baseline', label: 'Baseline Established' };
    const dt = avg6(todayScores) - avg6(yScores);
    if (Math.abs(dt) < 0.01) return { kind: 'hold', label: 'Held The Line' };
    if (dt > 0) return { kind: 'up', label: 'Imperial Momentum Sustained', delta: dt };
    // Find lowest pillar today for recalibrate
    const lowest = AURA.reduce((min, p) =>
      (todayScores[p.key] || 0) < (todayScores[min.key] || 0) ? p : min, AURA[0]);
    return { kind: 'down', label: `Recalibrate: ${lowest.label} Needs Upgrade`, delta: dt, pillar: lowest };
  }, [todayEntry, scores, yesterdayEntry, anyScored]);

  return (
    <div className="fixed inset-0 z-[220] flex flex-col overflow-hidden"
      style={{
        background: BG,
        paddingTop: 'max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 12px)',
      }} data-testid="tracker-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#E9C77A" testId="tracker-close-btn"/>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: '0 0 10px rgba(197,160,89,0.35)' }}>
          TRACKER
        </p>
        <span className="w-14"/>
      </div>

      {/* Sub-tabs — Aura Input / Aura Result */}
      <div className="flex-shrink-0 px-3 pt-2 pb-1.5 flex items-center gap-1.5">
        {[
          { id: 'input',  label: 'Aura Input' },
          { id: 'result', label: 'Aura Result' },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`tracker-subtab-${t.id}`}
              className="flex-1 py-1.5 rounded-lg font-['Cinzel'] uppercase tracking-[0.22em] transition-all"
              style={{
                background: active
                  ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`
                  : 'rgba(255,255,255,0.03)',
                color: active ? '#0a0a0a' : `${GOLD}CC`,
                border: `1px solid ${active ? GOLD_BRIGHT : GOLD + '44'}`,
                fontSize: 11,
                boxShadow: active ? `0 0 12px ${GOLD}66` : 'none',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ═══════════════ AURA INPUT TAB ═══════════════ */}
      {tab === 'input' && (
        <>
          <div className="flex-1 min-h-0 overflow-hidden px-3 pt-1 flex flex-col"
            data-testid="tracker-input-tab">
            {/* Mirror of the Empire — live summary of the 6 Aura pillars */}
            <div className="flex-shrink-0 rounded-xl px-2.5 py-2 mb-1.5"
              style={{ background: 'rgba(197,160,89,0.06)', border: `1px solid ${GOLD}44` }}
              data-testid="tracker-mirror">
              <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1.5"
                style={{ color: GOLD, fontSize: 9 }}>
                Mirror of the Empire
              </p>
              <div className="grid grid-cols-2 gap-x-2.5 gap-y-1">
                {AURA.map((p) => {
                  const v = scores[p.key] || 0;
                  return (
                    <div key={p.key} className="flex items-center gap-1.5"
                      data-testid={`tracker-mirror-${p.key}`}>
                      <span className="font-['Cinzel'] flex-shrink-0"
                        style={{ color: p.accent, fontSize: 9, width: 44,
                          textShadow: `0 0 4px ${p.accent}66` }}>
                        {p.label.toUpperCase()}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full"
                        style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div className="h-full rounded-full transition-all"
                          style={{
                            width: `${(v / 10) * 100}%`,
                            background: `linear-gradient(90deg, ${p.accent}, ${p.accent}77)`,
                            boxShadow: v ? `0 0 6px ${p.accent}88` : 'none',
                          }}/>
                      </div>
                      <span className="font-['Cinzel'] flex-shrink-0 text-right"
                        style={{ color: v ? p.accent : 'rgba(255,255,255,0.35)',
                          fontSize: 10, width: 14 }}>
                        {v || '–'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="font-['Cormorant_Garamond'] italic text-center flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, marginBottom: 4 }}>
              Score each pillar · {key}
            </p>

            <div className="flex-1 min-h-0 flex flex-col justify-between gap-1">
              {AURA.map((p) => (
                <ScoreRow key={p.key} pillar={p} value={scores[p.key]} onChange={setScore}/>
              ))}
            </div>
          </div>
          {/* Bottom dock — Aura Result button */}
          <div className="flex-shrink-0 px-3 pt-2 grid grid-cols-4 gap-2"
            style={{ paddingBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 24px) + 12px)' }}>
            <button onClick={clearToday}
              data-testid="tracker-clear-btn"
              disabled={!anyScored && !todayEntry}
              className="rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.2em]"
              style={{
                background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.1)', fontSize: 10,
                opacity: (!anyScored && !todayEntry) ? 0.4 : 1,
              }}>
              Clear
            </button>
            <button onClick={goToResult}
              data-testid="tracker-result-btn"
              disabled={!anyScored}
              className="col-span-3 rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em] transition-all"
              style={{
                background: allScored
                  ? `linear-gradient(135deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
                  : `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
                color: '#0a0a0a', fontSize: 12,
                border: `1px solid ${GOLD}`,
                boxShadow: allScored
                  ? `0 0 22px ${GOLD_BRIGHT}, inset 0 1px 0 rgba(255,255,255,0.35)`
                  : `0 0 14px ${GOLD}55, inset 0 1px 0 rgba(255,255,255,0.2)`,
                opacity: !anyScored ? 0.4 : 1,
              }}>
              ✦ Aura Result
            </button>
          </div>
        </>
      )}

      {/* ═══════════════ AURA RESULT TAB ═══════════════ */}
      {tab === 'result' && (
        <div className="flex-1 min-h-0 flex flex-col px-3 pt-1 gap-1.5"
          style={{ paddingBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 24px) + 12px)' }}
          data-testid="tracker-result-tab">
          {/* Verdict pill */}
          {verdict && (
            <div className="flex-shrink-0 rounded-lg px-3 py-1.5 text-center"
              data-testid="tracker-verdict"
              style={{
                background: verdict.kind === 'up'
                  ? 'linear-gradient(135deg, rgba(42,175,84,0.20), rgba(20,143,60,0.10))'
                  : verdict.kind === 'down'
                  ? 'linear-gradient(135deg, rgba(226,74,31,0.20), rgba(176,31,31,0.10))'
                  : 'rgba(197,160,89,0.12)',
                border: `1px solid ${
                  verdict.kind === 'up' ? '#2AAF54'
                  : verdict.kind === 'down' ? '#E24A1F'
                  : GOLD}`,
              }}>
              <p className="font-['Cinzel'] uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
                style={{
                  color: verdict.kind === 'up' ? '#8ED847'
                    : verdict.kind === 'down' ? '#FF7A50'
                    : GOLD_BRIGHT,
                  fontSize: 11,
                }}>
                {verdict.kind === 'up' && <TrendingUp size={13}/>}
                {verdict.kind === 'down' && <TrendingDown size={13}/>}
                {(verdict.kind === 'hold' || verdict.kind === 'baseline') && <Minus size={13}/>}
                {verdict.label}
                {verdict.delta !== undefined && (
                  <span style={{ opacity: 0.7, fontSize: 10 }}>
                    · {verdict.delta > 0 ? '+' : ''}{verdict.delta.toFixed(2)}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Top: Today */}
          <ResultGraph title="Today"
            scores={store[key]?.scores || scores}
            compareTo={yesterdayEntry?.scores}
            positiveWhenHigher={true}/>

          {/* Bottom: Yesterday */}
          <ResultGraph title="Yesterday"
            scores={yesterdayEntry?.scores || emptyScores()}
            compareTo={null}
            positiveWhenHigher={false}/>
        </div>
      )}
    </div>
  );
};

/* Tile icon — radar diamond + ascending pulse */
export const TrackerTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(233,199,122,0.6))' }}>
    <defs>
      <linearGradient id="trkIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#FFE9A0"/>
        <stop offset="0.5" stopColor="#C5A059"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
    </defs>
    <polygon points="12,3 21,12 12,21 3,12"
      fill="none" stroke="url(#trkIcon)" strokeWidth="1.3"/>
    <polygon points="12,7 17,12 12,17 7,12"
      fill="none" stroke="url(#trkIcon)" strokeWidth="1" opacity="0.6"/>
    <polyline points="5,15 9,13 13,10 17,7 20,5"
      fill="none" stroke="url(#trkIcon)" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="20" cy="5" r="1.4" fill="url(#trkIcon)"/>
  </svg>
);

export default TheTracker;
